import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import remove from "../../../images/remove.svg";
import darkremove from "../../../images/darkremove.svg";
import close from "../../../images/close.svg";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUser,
  setLoginModal,
  updateUser,
} from "../../../store/auth-slice";
import { useCreateSportsBetMutation } from "../../../store/apis";
import {
  clearBetSlip,
  removeBet,
  setStake,
} from "../../../store/betslip-slice";
import { toastService } from "../../notificationPage/ToastContainer";
import { determineMarketOutcome } from "../../../utils/odds";
import {
  Plane,
  Gamepad2,
  Flame,
  Target,
  MessageCircle,
  FileText,
  X,
} from "lucide-react";

const BetSlipMobile = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [isToggled, setIsToggled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { bets, totalOdds, totalStake, possibleWin } = useSelector(
    (state) => state.betSlip
  );
  const user = useSelector(selectUser);
  const [createBet, { isLoading }] = useCreateSportsBetMutation();

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleToggle = () => {
    setIsToggled(!isToggled);
  };

  const handleToggles = () => {
    setIsOpen(!isOpen);
  };

  const goToBetHistory = () => {
    navigate("/bet-history");
  };

  const handleRemoveBet = (match, odd, market) => {
    dispatch(removeBet({ match: match, odd, market }));
  };

  const handleStakeSelection = (stake) => {
    dispatch(setStake(stake));
  };

  const handleClearBetSlip = () => {
    dispatch(clearBetSlip());
  };

  // Tax and calculation constants
  const EXCISE_TAX_RATE = 0.15;
  const WITHHOLDING_TAX_RATE = 0.2;

  const calculateTaxes = () => {
    const winAmount = possibleWin;
    const exciseTax = totalStake * EXCISE_TAX_RATE;
    const withholdingTax = winAmount * WITHHOLDING_TAX_RATE;
    const netPayout = winAmount - withholdingTax;

    return {
      exciseTax: exciseTax.toFixed(2),
      withholdingTax: withholdingTax.toFixed(2),
      netPayout: netPayout.toFixed(2),
    };
  };

  const handlePlaceBet = async () => {
    if (!user) {
      dispatch(setLoginModal(true));
      toastService.error("Error! Please login to proceed!!");
      return;
    }
    if (totalStake < 1) {
      toastService.error("Error! Invalid amount!!");
      return;
    }
    if (totalStake > user.balance) {
      toastService.error("Error! Insufficient funds!!");
      return;
    }
    if (bets.length === 0) {
      toastService.error("Error! Empty bets");
      return;
    }

    const betData = bets.map((bet) => {
      const marketOutcome = determineMarketOutcome(
        bet.odd.label,
        bet.match.name
      );
      const oddLabelName =
        marketOutcome.type === "Team" ? marketOutcome.team : marketOutcome.type;
      return {
        customer_id: user.id,
        match_id: bet.match.id,
        game_name: bet.match.name,
        label: bet.odd.label,
        odd_label: oddLabelName,
        market_name: bet.match.market,
        event_date: bet.match.date,
        odd_value: bet.odd.value,
        stake_amount: totalStake,
        potential_payout: possibleWin,
        status: "pending",
      };
    });

    try {
      console.log("BETDATE", betData);
      const response = await createBet(betData).unwrap();
      toastService.success(`${response.betSlipId} ${response.message}`);
      dispatch(updateUser(response.user));
      // Clear the bet slip after placing the bet
      dispatch(clearBetSlip());
      // Close the mobile betslip
      setIsOpen(false);
    } catch (error) {
      console.error("Error placing bet:", error);
      toastService.error("Error placing bet:");
    }
  };

  const taxes = calculateTaxes();
  const hasBets = bets && bets.length > 0;

  // Navigation items with Lucide icons
  const navItems = [
    {
      name: "AVIATOR",
      icon: <Plane size={20} />,
      path: "/game/avaitor?provider=aviator&mode=real",
    },
    { name: "GAMES", icon: <Gamepad2 size={20} />, path: "/casino" },
    { name: "PROMO", icon: <Flame size={20} />, path: "/" },
    {
      name: "VIRTUALS",
      icon: <Target size={20} />,
      path: "/game/gfhjdghvfdvsaddd?provider=eurovirtual&mode=real",
    },
    { name: "CHAT", icon: <MessageCircle size={20} />, path: "/" },
  ];

  return (
    <>
      {/* New Bottom Navigation Bar for Mobile */}
      <div className="s15:hidden flex justify-between items-center fixed bottom-0 z-[9] w-full bg-charcoal p-2">
        {navItems.map((item, index) => (
          <Link
            key={index}
            to={item.path}
            className="flex flex-col items-center px-2 py-1"
          >
            <span className="text-green-400">{item.icon}</span>
            <span className="text-white text-xs">{item.name}</span>
          </Link>
        ))}
      </div>

      {/* Betslip Button - Always visible */}
      <div className="s15:hidden flex justify-center items-center fixed bottom-16 right-4 z-[10]">
        <button
          onClick={handleToggles}
          className="bg-forestGreen rounded-full p-3 shadow-lg relative"
        >
          <FileText size={22} className="text-white" />
          {hasBets && (
            <span className="absolute -top-2 -right-2 bg-yellow text-darkblue rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
              {bets.length}
            </span>
          )}
        </button>
      </div>

      {/* Betslip Content */}
      <div className={`open-content ${isOpen ? "block" : "hidden"}`}>
        <div className="odd-conected rounded-[7px] overflow-auto max-h-[70vh] block fixed bottom-20 z-[9] max-w-[428px] mx-auto left-1/2 -translate-x-1/2 s15:hidden pt-5 w-[95%]">
          <div className="rounded-[7px] bg-charcoal shadow-dashboardshadow w-full">
            <div className="flex items-center justify-between rounded-[7px_7px_0px_0px] bg-forestGreen px-[15px]">
              <span className="cursor-pointer w-[50%] flex items-center gap-[7px] p-[10px_0px] text-white text-base s18:text-lg font-medium leading-none">
                <span className="flex w-8 h-8 justify-center items-center bg-yellow rounded-50 text-black text-base s18:text-lg font-medium leading-none">
                  {bets.length}
                </span>
                Betslip
              </span>
              <span
                className="show-content cursor-pointer w-[50%] flex justify-center items-center p-[10px_0px] text-white text-base s18:text-lg font-medium leading-none"
                onClick={goToBetHistory}
              >
                My bets
              </span>
              <button
                onClick={handleToggles}
                className="absolute top-2 right-2 p-1 text-white"
              >
                <X size={18} />
              </button>
            </div>
            {hasBets ? (
              <>
                <span
                  className="cursor-pointer border-b-[2px] border-b-lightwhiteborder flex p-[12px] bg-teal items-center gap-3 text-[#EF4444] text-sm font-medium leading-tight"
                  onClick={handleClearBetSlip}
                >
                  <img src={remove} alt="remove" />
                  Remove all
                </span>
                {bets.map((bet, index) => {
                  const marketOutcome = determineMarketOutcome(
                    bet.odd.label,
                    bet.match.name
                  );
                  return (
                    <div
                      key={index}
                      className="flex justify-between p-[15px_12px_10px] border-solid border-b-[2px] border-b-lightwhiteborder"
                    >
                      <div className="flex gap-3 items-start">
                        <img
                          src={darkremove}
                          alt="remove"
                          className="cursor-pointer mt-[3px]"
                          onClick={() =>
                            handleRemoveBet(bet.match, bet.odd, bet.market)
                          }
                        />
                        <div className="flex flex-col gap-[5px] cursor-pointer">
                          <p className="text-white text-sm max-[1439px]:text-base font-medium leading-tight">
                            {marketOutcome.type === "Team"
                              ? marketOutcome.team
                              : marketOutcome.type}
                          </p>
                          <p className="text-white text-sm max-[1439px]:text-base font-normal leading-tight">
                            {bet.match.market}
                          </p>
                          <p className="text-white text-xs xl:text-sm font-normal leading-tight">
                            {bet.match.name}
                          </p>
                          <p className="text-white text-xs xl:text-sm font-normal leading-tight">
                            {bet.match.event_date}
                          </p>
                        </div>
                      </div>
                      <span className="h-full rounded-md flex justify-center items-center bg-yellow p-[5px_9px] text-darkblue text-sm max-[1439px]:text-base font-semibold leading-tight">
                        {bet.odd.value}
                      </span>
                    </div>
                  );
                })}
                <div className="p-[15px_15px_0px]">
                  <span className="text-[#EF4444] text-sm max-[1439px]:text-base font-medium leading-12">
                    Stake
                  </span>
                  <div className="flex gap-[5px] mt-[5px]">
                    {[20, 50, 100, 500].map((stakeAmount) => (
                      <span
                        key={stakeAmount}
                        onClick={() => handleStakeSelection(stakeAmount)}
                        className="cursor-pointer p-[8px_10px] max-[1439px]:p-[12px_14px] max-[1439px]:w-[58px] max-[1439px]:h-[43px] flex justify-center items-center rounded-md border text-sm max-[1439px]:text-base font-medium leading-12 hover:border-bordercolor transition-all duration-300 bg-forestGreen border-doubleextralightwhite text-white hover:bg-yellow hover:text-darkblue"
                      >
                        {stakeAmount}/-
                      </span>
                    ))}
                    <span className="flex justify-end p-[12px_14px] items-center w-full h-[43px] rounded-md text-lg font-semibold leading-[120%] text-yellow border bg-forestGreen border-doubleextralightwhite">
                      {totalStake}
                    </span>
                  </div>
                  <div className="p-[15px_0px] flex flex-col gap-[10px]">
                    <p className="flex justify-between items-center text-white text-sm max-[1439px]:text-base font-normal leading-12">
                      Total Odds{" "}
                      <span className="text-white text-sm max-[1439px]:text-base font-semibold leading-tight">
                        {totalOdds.toFixed(2)}
                      </span>
                    </p>
                    <p className="flex justify-between items-center text-white text-sm max-[1439px]:text-base font-normal leading-12">
                      Possible Win{" "}
                      <span className="flex gap-[5px] text-white text-sm max-[1439px]:text-base font-semibold leading-tight">
                        <span className="text-sm max-[1439px]:text-base text-extralightwhite font-semibold leading-tight">
                          KShs.
                        </span>{" "}
                        {possibleWin.toFixed(2)}
                      </span>
                    </p>
                    <p className="flex justify-between items-center text-white text-sm max-[1439px]:text-base font-normal leading-12">
                      15% Excise Tax{" "}
                      <span className="flex gap-[5px] text-white text-sm max-[1439px]:text-base font-semibold leading-tight">
                        <span className="text-sm max-[1439px]:text-base font-semibold leading-tight text-extralightwhite">
                          KShs.
                        </span>{" "}
                        {taxes.exciseTax}
                      </span>
                    </p>
                  </div>
                  <div className="rounded-[0px_0px_7px_7px] bg-forestGreen p-[15px_15px_20px] flex flex-col gap-[10px] mx-[-15px]">
                    <p className="flex justify-between items-center text-white text-sm max-[1439px]:text-base font-normal leading-12">
                      Win{" "}
                      <span className="text-white text-sm max-[1439px]:text-base font-semibold leading-tight">
                        <span className="text-sm max-[1439px]:text-base text-extralightwhite font-semibold leading-tight">
                          KShs.
                        </span>{" "}
                        {possibleWin.toFixed(2)}
                      </span>
                    </p>
                    <p className="flex justify-between items-center text-white text-sm max-[1439px]:text-base font-normal leading-12">
                      20% Withholding Tax{" "}
                      <span className="flex gap-[5px] text-white text-sm max-[1439px]:text-base font-semibold leading-tight">
                        <span className="text-sm max-[1439px]:text-base text-extralightwhite font-semibold leading-tight">
                          KShs.
                        </span>{" "}
                        {taxes.withholdingTax}
                      </span>
                    </p>
                    <p className="flex justify-between items-center text-white text-sm max-[1439px]:text-base font-normal leading-12">
                      Possible Payout{" "}
                      <span className="flex gap-[5px] text-white text-base font-semibold leading-tight">
                        <span className="text-sm max-[1439px]:text-base font-semibold leading-tight text-extralightwhite">
                          KShs.
                        </span>{" "}
                        {taxes.netPayout}
                      </span>
                    </p>
                    <div className="flex justify-between items-center mt-4 max-[1439px]:mt-[40px]">
                      <label className="flex items-center text-xs max-[1439px]:text-sm font-normal leading-tight text-extralightwhite select-none">
                        <input
                          type="checkbox"
                          className="hidden peer"
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                        />
                        <span className="mr-[9px] -mt-[2px] max-[1439px]:mt-0 w-3 max-[1439px]:w-[18px] h-3 max-[1439px]:h-[18px] rounded-[2px] border bg-transparent border-solid border-[#A3A3A3] peer-checked:bg-green-600 peer-checked:border-green-600 flex justify-center items-center">
                          {isChecked ? (
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="text-white ml-[1px] font-bold opacity-100"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="text-gray-400 opacity-0"
                            />
                          )}
                        </span>
                        Accept odd changes
                      </label>
                      <div className="flex items-center gap-[9px]">
                        <div
                          onClick={handleToggle}
                          className={`relative w-7 h-4 max-[1439px]:w-[30px] max-[1439px]:h-5 flex items-center rounded-[11px] cursor-pointer ${
                            isToggled ? "bg-[#11A306]" : "bg-[#A3A3A3]"
                          }`}
                        >
                          <div
                            className={`w-3 h-3 max-[1439px]:w-[14px] max-[1439px]:h-[14px] bg-white rounded-[11px] transform transition-transform ${
                              isToggled ? "translate-x-3" : "translate-x-1"
                            }`}
                          ></div>
                        </div>
                        <span className="text-xs max-[1439px]:text-sm font-medium leading-tight text-extralightwhite">
                          Keep betslip
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-[9px] mt-[15px] md:mt-3 s18:mt-[15px]">
                      <Link
                        to="/"
                        className="inline-flex border-2 border-solid border-white hover:bg-transparent hover:text-white transition-all duration-300 bg-white rounded-[7px] text-darkblue p-[16px_17px] md:p-[10px_14px] s18:p-[16px_17px] text-sm font-medium leading-[111%]"
                      >
                        Share
                      </Link>
                      <button
                        disabled={isLoading}
                        onClick={handlePlaceBet}
                        className={`rounded-[7px] cursor-pointer flex justify-center items-center w-full bg-yellow p-[15px] md:p-[10px_14px] s18:p-[15px] text-darkblue text-sm font-medium leading-[111%] ${
                          isLoading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {isLoading ? (
                          <div className="flex items-center">
                            <svg
                              className="animate-spin h-4 w-4 mr-2 text-darkblue"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l3 3-3 3v-4a8 8 0 01-8-8z"
                              ></path>
                            </svg>
                            Placing Bet...
                          </div>
                        ) : (
                          <>
                            Place Bet KShs. <span>{totalStake}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4 border-b border-extrathinblack dark:border-lightwhiteborder">
                <div className="w-14 h-14 mb-4 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#2F4058]">
                  <svg
                    className="w-7 h-7 text-gray-400 dark:text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-darkblue dark:text-white mb-2">
                  No Selection
                </h3>
                <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-4">
                  Please select odds from the available markets to add to your
                  betslip.
                </p>
                <button
                  onClick={goToBetHistory}
                  className="px-4 py-2 bg-darkblue text-white dark:bg-yellow dark:text-darkblue rounded-md hover:bg-opacity-90 dark:hover:bg-opacity-90 transition-all"
                >
                  View Bet History
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BetSlipMobile;
